import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { DateTime } from 'luxon'
import { useWidget } from '../../../providers/widget'
import { useFetchWidget } from '../../../hooks'
import { getProxiedImageUrl } from '../../../common/image-proxy'
import { propertyLinkRel, resolvePropertyLink, withStayParams } from '../../../common/property-href'
import { usePropertyHrefFeed } from '../../../common/use-property-href-feed'
import { formatPrice as formatPriceIntl } from '../../../common/utils'
import { AvailabilityDataRecord } from '../../../types'
import { CalendarUtils } from '../../calendar/calendar-utils'
import { MultiCalendarWidgetConfig } from '../config'
import { HEADER_COL, clampToShape, isGridNavigationKey, nextCell, type GridCell } from '../keyboard'
import { buildDateRange, useFeedAvailability, propertyCurrency, MultiCalProperty, MultiCalDay } from '../data'

// Narrow currency symbol ("$200", never "US$200" on en-GB/en — #599).
function formatPrice(amount: number, currency: string): string {
  return formatPriceIntl(Math.round(amount), currency)
}

// Map a CalendarUtils validation message to visitor-friendly copy.
function friendlyMessage(msg?: string): string {
  if (!msg) return 'These dates aren’t available.'
  const minStay = msg.match(/^Minimum stay is (\d+)/)
  if (minStay) return `Can’t check out, this home requires a ${minStay[1]} night minimum`
  if (/^Maximum stay/.test(msg)) return msg
  if (/Unavailable dates in range/i.test(msg)) return 'Some nights in that range aren’t available.'
  if (/Checkout/i.test(msg)) return 'That checkout date isn’t available.'
  if (/past/i.test(msg)) return 'That date is in the past.'
  if (msg.startsWith('advance_notice:')) {
    const days = msg.split(':')[1]
    return `This home needs at least ${days} day${days === '1' ? '' : 's'} notice to book.`
  }
  return 'That date isn’t available for check-in.'
}

type Selection = { propId: string; checkin: string; checkout: string | null }
/** Which cell the popup was opened from, so focus can be handed back. */
type PopupOrigin = GridCell | null
type Popup = { propId: string; top: number; left: number }
type Notice = { text: string; type: 'warning' | 'danger' }

export default function MultiCalendarGrid() {
  const { options, feedId, widgetId } = useWidget<MultiCalendarWidgetConfig>()
  const { data: widgetData } = useFetchWidget(feedId, widgetId)
  const routes = widgetData?.feed?.additional_info?.reverseProxy?.routes
  const hrefFeed = usePropertyHrefFeed(widgetData?.feed)
  const pdpRoute = routes?.pdp || '/property/[slug]'

  const selectable = options.allowSelectDates === true

  const filter = useMemo(() => ({
    feed_id: options.filter?.feed_id ?? feedId,
    platform: options.filter?.platform ?? undefined,
  }), [options.filter, feedId])

  // Date range — startDate or today, spanning daysToShow.
  const { startKey, endKey, dates } = useMemo(() => {
    const base = options.startDate
      ? DateTime.fromISO(options.startDate)
      : DateTime.now()
    const start = (base.isValid ? base : DateTime.now()).startOf('day').toJSDate()
    const keys = buildDateRange(start, options.daysToShow)
    return { startKey: keys[0], endKey: keys[keys.length - 1], dates: keys }
  }, [options.startDate, options.daysToShow])

  const {
    data,
    isLoading,
    isError,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useFeedAvailability(filter, startKey, endKey, options.perPage)

  const properties: MultiCalProperty[] = useMemo(
    () => data?.pages.flatMap((p) => p.result) ?? [],
    [data],
  )

  // One CalendarUtils per property row (reused for check-in/checkout validation).
  // maxNumDays is generous so the visible window is always inside [today, today+N];
  // missing days are treated as unavailable by CalendarUtils.
  const utilsByProp = useMemo(() => {
    const m = new Map<string, CalendarUtils>()
    if (!selectable) return m
    for (const p of properties) {
      // NOTE (#1071): no advanceNoticeHours here, so this widget still accepts a
      // check-in the property's advance-notice rule rejects at checkout — the
      // calendar widget enforces it, this one can't yet. Not an oversight: the
      // /feed/availability payload (MultiCalProperty) carries no
      // advance_notice_hours, so central has to expose it per property before
      // this can be wired. friendlyMessage() below is ready for the message.
      const u = new CalendarUtils({ timezone: 'UTC', minStay: 1, maxStay: 365, maxNumDays: 730 })
      u.setPriceData(p.availability as unknown as AvailabilityDataRecord)
      m.set(p.id, u)
    }
    return m
  }, [properties, selectable])

  // Selection / interaction state.
  const wrapRef = useRef<HTMLDivElement | null>(null)
  const [selection, setSelection] = useState<Selection | null>(null)
  const [hoverDate, setHoverDate] = useState<string | null>(null)
  const [popup, setPopup] = useState<Popup | null>(null)
  const [notice, setNotice] = useState<Notice | null>(null)
  const noticeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  /* Keyboard access (#1582). Every hook below sits ABOVE the loading/error/
     empty early returns — the grid renders a spinner before the data lands, so
     a hook declared next to the JSX it belongs to would change the hook count
     between those two renders and throw on the only path that matters. */

  /** The cell the roving tabstop remembers. Null until the user arrives. */
  const [focusCell, setFocusCell] = useState<GridCell | null>(null)
  /** Set when a key moved focus, consumed by the effect that pulls DOM focus. */
  const pendingFocusRef = useRef<GridCell | null>(null)
  const popupRef = useRef<HTMLDivElement | null>(null)
  /** Where focus goes when the popup closes. */
  const popupOriginRef = useRef<PopupOrigin>(null)
  /** Paused while the close button holds focus, so the timer cannot yank it. */
  const noticeHoverRef = useRef(false)

  const shape = useMemo(
    () => ({ rowCount: properties.length, colCount: dates.length }),
    [properties.length, dates.length],
  )

  /* DERIVED, not stored. "Load more" appends rows and a new date window
     replaces the list wholesale, so a remembered cell can point past the end;
     clamping keeps the user roughly in place instead of throwing them to the
     first cell, and guarantees exactly one cell claims tabIndex 0 even while
     the list is changing under it. */
  const tabstop = useMemo(
    () => clampToShape(focusCell ?? { row: 0, col: 0 }, shape),
    [focusCell, shape],
  )

  const cellId = (row: number, col: number) => `${row}:${col}`
  const focusCellEl = useCallback((cell: GridCell) => {
    const el = wrapRef.current?.querySelector(
      `[data-hr-cell="${cellId(cell.row, cell.col)}"]`,
    ) as HTMLElement | null
    if (!el) return
    // preventScroll, then scroll the SCROLLER minimally: the grid lives in an
    // overflow container with a sticky header, and letting the browser do it
    // drags the whole page.
    el.focus({ preventScroll: true })
    el.scrollIntoView({ block: 'nearest', inline: 'nearest' })
  }, [])

  // Pull DOM focus to wherever the keys just moved the tabstop.
  useEffect(() => {
    const want = pendingFocusRef.current
    if (!want) return
    pendingFocusRef.current = null
    focusCellEl(want)
  })

  const showNotice = useCallback((text: string, type: Notice['type'] = 'warning') => {
    setNotice({ text, type })
    if (noticeTimer.current) clearTimeout(noticeTimer.current)
    // Do not unmount the notice while its own close button holds focus: the
    // 4.5s timer would drop the keyboard user to <body> with no interaction at
    // all. Re-arm when focus leaves instead.
    noticeTimer.current = setTimeout(function tick() {
      if (noticeHoverRef.current) {
        noticeTimer.current = setTimeout(tick, 1000)
        return
      }
      setNotice(null)
    }, 4500)
  }, [])

  useEffect(() => () => { if (noticeTimer.current) clearTimeout(noticeTimer.current) }, [])

  const clearSelection = useCallback(() => {
    setSelection(null)
    setHoverDate(null)
    setPopup(null)
  }, [])

  /* Takes the ELEMENT, not the event, so the keyboard can activate a cell the
     same way the pointer does — the only thing the event was ever used for is
     the popup's anchor rect. */
  const handleCellClick = useCallback((propId: string, key: string, target: HTMLElement) => {
    if (!selectable) return
    const utils = utilsByProp.get(propId)
    if (!utils) return

    // Start a fresh selection: nothing selected, a different row, or a completed range.
    if (!selection || selection.propId !== propId || selection.checkout) {
      const ci = utils.canCheckin(key)
      if (!ci.status) { showNotice(friendlyMessage(ci.message)); return }
      setSelection({ propId, checkin: key, checkout: null })
      setPopup(null)
      setHoverDate(null)
      return
    }

    // Completing a selection (checkin set, choosing checkout on the same row).
    const checkin = selection.checkin
    if (key <= checkin) {
      // Clicking at/before the check-in restarts the range from here.
      const ci = utils.canCheckin(key)
      if (!ci.status) { showNotice(friendlyMessage(ci.message)); return }
      setSelection({ propId, checkin: key, checkout: null })
      setHoverDate(null)
      return
    }
    const res = utils.canCheckinout(checkin, key)
    if (!res.status) { showNotice(friendlyMessage(res.message)); return }

    const cellRect = target.getBoundingClientRect()
    const wrapRect = wrapRef.current?.getBoundingClientRect()
    setSelection({ propId, checkin, checkout: key })
    setHoverDate(null)
    setPopup({
      propId,
      top: cellRect.bottom - (wrapRect?.top ?? 0),
      left: Math.max(4, cellRect.left - (wrapRect?.left ?? 0)),
    })
  }, [selectable, utilsByProp, selection, showNotice])

  const handleCellHover = useCallback((propId: string, key: string) => {
    if (!selectable) return
    if (selection && selection.propId === propId && !selection.checkout && key > selection.checkin) {
      setHoverDate(key)
    }
  }, [selectable, selection])

  /* Closing the popup also CLEARS the selection, and both Escape and an
     outside press route through here. Closing it alone would leave a completed
     range painted with its only Reserve affordance gone — the next click takes
     the `selection.checkout` branch and restarts the range, so the popup never
     comes back. Cancel means cancel. */
  const closePopup = useCallback(() => {
    const origin = popupOriginRef.current
    /* Restore focus only if the popup HELD it. A pointer user who clicks away
       never had focus in there, and pulling it back would move their caret and
       scroll the grid into view for a dismissal they never asked for. */
    const root = popupRef.current?.getRootNode() as Document | ShadowRoot | undefined
    const hadFocus = !!(
      popupRef.current && root?.activeElement && popupRef.current.contains(root.activeElement)
    )
    popupOriginRef.current = null
    setPopup(null)
    setSelection(null)
    setHoverDate(null)
    if (origin && hadFocus) {
      setFocusCell(origin)
      pendingFocusRef.current = origin
    }
  }, [])

  // Move focus into the popup when it opens: it is the next action, and a
  // keyboard user who has just completed a range is otherwise stranded on the
  // cell with the buttons unreachable.
  useEffect(() => {
    if (!popup) return
    const first = popupRef.current?.querySelector('button') as HTMLElement | null
    first?.focus()
  }, [popup])

  /* Outside-press dismissal. `composedPath()` and NOT `node.contains(target)`:
     the widget mounts in a shadow root in production, where an event seen at
     document level reports the shadow HOST as its target — so `contains` would
     read every press, including one on the popup's own Reserve button, as
     "outside" and dismiss it. In a light-DOM test both spellings pass, which is
     why the test for this mounts inside a real shadow root. */
  useEffect(() => {
    if (!popup) return
    const onDown = (ev: Event) => {
      const path = ev.composedPath()
      if (popupRef.current && path.includes(popupRef.current)) return
      closePopup()
    }
    document.addEventListener('pointerdown', onDown, true)
    return () => document.removeEventListener('pointerdown', onDown, true)
  }, [popup, closePopup])

  const handleReserve = useCallback(() => {
    if (!selection || !selection.checkout) return
    const p = properties.find((x) => x.id === selection.propId)
    if (!p || !p.slug) { showNotice('This property can’t be reserved online.', 'danger'); return }
    // #1714: a property the feed maps to its own page (External Property Pages) reserves there.
    const link = resolvePropertyLink({ feed: hrefFeed, slug: p.slug, pdpRoute })
    window.location.href = withStayParams(link, `checkin=${selection.checkin}&checkout=${selection.checkout}`)
  }, [selection, properties, hrefFeed, pdpRoute, showNotice])

  /** Spoken name for a cell: the date, the property, the price, the state. */
  const dateLabels = useMemo(() => {
    // Per COLUMN, not per cell. A 50x92 grid is 4,600 cells and this used to be
    // zero date work per cell; formatting inside the cell would re-run the lot
    // on every hover, focus and selection change.
    const m = new Map<string, string>()
    for (const key of dates) m.set(key, DateTime.fromISO(key).toFormat('cccc d LLLL yyyy'))
    return m
  }, [dates])

  const handleGridKeyDown = useCallback((cell: GridCell, e: React.KeyboardEvent<HTMLElement>) => {
    /* Never consume a modified key. Cmd+Left is Back on macOS, Alt+Left is Back
       elsewhere, and Ctrl/Cmd+Home scrolls the document — swallowing those to
       move a cell would take working browser shortcuts away from exactly the
       users this change is for. The grid only claims the bare keys. */
    if (e.ctrlKey || e.metaKey || e.altKey) return

    if (e.key === 'Escape') {
      if (popup) { e.preventDefault(); closePopup(); return }
      // Escape must also drop a HALF-made selection. Only the popup listened
      // before, so a user who picked a check-in and changed their mind had no
      // way back — the check-in stayed painted and the next click on or before
      // it silently restarted the range instead of cancelling.
      if (selection) { e.preventDefault(); clearSelection() }
      return
    }
    if (isGridNavigationKey(e.key)) {
      const next = nextCell(e.key, cell, shape)
      if (!next) return
      e.preventDefault()
      setFocusCell(next)
      pendingFocusRef.current = next
      return
    }
    if (e.key === 'Enter' || e.key === ' ') {
      /* Space scrolls the document by default, and every cell here is
         focusable — including unavailable and empty ones. Swallow it on every
         path, or pressing Space while reading a booked night jumps the page. */
      if (e.key === ' ') e.preventDefault()
      if (cell.col === HEADER_COL) {
        /* The focusable element is the <th>, and the <a> inside it is
           tabIndex -1 so it cannot compete for the grid's single tabstop —
           which means the browser will not activate it for us. Forward the
           activation, or folding the header into the roving model would have
           taken away a link the keyboard could reach before this change. */
        const link = (e.currentTarget as HTMLElement).querySelector('a')
        if (link) { e.preventDefault(); link.click() }
        return
      }
      const propId = properties[cell.row]?.id
      const key = dates[cell.col]
      if (!propId || !key) return
      const day = properties[cell.row]?.availability[key]
      if (!selectable || !day || day.available <= 0) return
      e.preventDefault()
      popupOriginRef.current = cell
      handleCellClick(propId, key, e.currentTarget as HTMLElement)
    }
  }, [popup, closePopup, selection, clearSelection, shape, properties, dates, selectable, handleCellClick])

  // First valid checkout while picking one — dates before it (after check-in)
  // are inside the min-stay window and gray out like the WP plugin (#603).
  const minStayEdge = selection && !selection.checkout
    ? utilsByProp.get(selection.propId)?.getSelectableRange(selection.checkin)?.minDate ?? null
    : null

  // Cell selection-state class (string keys sort chronologically).
  const cellSelClass = (propId: string, key: string): string => {
    if (!selection || selection.propId !== propId) return ''
    const { checkin, checkout } = selection
    if (key === checkin) return ' is-selected is-selected-start'
    if (checkout) {
      if (key === checkout) return ' is-selected is-selected-end'
      if (key > checkin && key < checkout) return ' is-range'
    } else {
      let cls = ''
      if (hoverDate && key > checkin && key <= hoverDate) cls += ' is-hover-preview'
      if (minStayEdge && key > checkin && key < minStayEdge) cls += ' is-minstay-blocked'
      return cls
    }
    return ''
  }

  if (isLoading) {
    return (
      <div className="hr-multical-loading">
        <div className="hr-multical-spinner" />
      </div>
    )
  }

  if (isError) {
    return (
      <div className="hr-multical-notice is-danger" role="alert">
        Couldn’t load availability. Please try again.
      </div>
    )
  }

  if (!properties.length) {
    return <div className="hr-multical-empty">{options.noItemsText || 'No properties found.'}</div>
  }

  // Month spans for the header (groups consecutive dates by month).
  const monthSpans: { label: string; count: number }[] = []
  for (const key of dates) {
    const label = DateTime.fromISO(key).toFormat('LLLL yyyy')
    const last = monthSpans[monthSpans.length - 1]
    if (last && last.label === label) last.count += 1
    else monthSpans.push({ label, count: 1 })
  }

  /** What a screen reader says for one cell. `priceText` is passed in rather
   *  than formatted here: the cell already computed it for the visible span,
   *  and `Intl.NumberFormat` is the expensive part — formatting twice per cell
   *  doubles it across the whole grid on every render. */
  const cellLabel = (
    propTitle: string, key: string, day: MultiCalDay | undefined, selClass: string, priceText: string,
  ): string => {
    const date = dateLabels.get(key) ?? key
    if (!day) return `${date}, ${propTitle}, no availability information`
    const parts = [date, propTitle]
    // An explicit aria-label SUPPRESSES the cell's own price text for AT, so a
    // selectable grid would otherwise be less informative than the read-only
    // one. Empty when the operator has hidden prices — the label must not
    // announce what the screen does not show.
    if (priceText) {
      parts.push(priceText)
    }
    parts.push(day.available > 0 ? 'available' : 'booked')
    if (selClass.includes('is-selected-start')) parts.push('selected check-in')
    else if (selClass.includes('is-selected-end')) parts.push('selected check-out')
    else if (selClass.includes('is-range')) parts.push('in selected range')
    else if (selClass.includes('is-minstay-blocked')) parts.push('shorter than the minimum stay')
    return parts.join(', ')
  }

  const renderCell = (
    propId: string, propTitle: string, key: string, day: MultiCalDay | undefined,
    currency: string, row: number, col: number,
  ) => {
    const isAvailable = !!day && day.available > 0
    const selClass = day ? cellSelClass(propId, key) : ''
    const interactive = selectable && isAvailable
    const isTabstop = selectable && tabstop?.row === row && tabstop?.col === col
    const selected = selClass.includes('is-selected') || selClass.includes('is-range')
    // Formatted ONCE, used by both the visible span and the accessible name.
    const priceText = isAvailable && !options.hidePrice && day!.price > 0
      ? formatPrice(day!.price, currency)
      : ''

    /* The empty-cell early return is folded in rather than dropped: a missing
       day still has to be a gridcell so the row stays traversable, but it keeps
       hr-multical-cell-empty and gains no interactivity. */
    const classes = day
      ? `hr-multical-cell ${isAvailable ? 'is-available' : 'is-booked'}${selClass}${interactive ? ' is-interactive' : ''}`
      : 'hr-multical-cell hr-multical-cell-empty'

    return (
      <td
        key={key}
        className={classes}
        {...(selectable ? {
          role: 'gridcell',
          'data-hr-cell': `${row}:${col}`,
          tabIndex: isTabstop ? 0 : -1,
          'aria-label': cellLabel(propTitle, key, day, selClass, priceText),
          'aria-disabled': interactive ? undefined : true,
          'aria-selected': selected,
          onKeyDown: (e: React.KeyboardEvent<HTMLElement>) => handleGridKeyDown({ row, col }, e),
          onFocus: () => {
            // Only when it actually changed. An arrow key already set this, and
            // the focus it causes would otherwise set it again — two full grid
            // renders per keypress, on the path that has to feel instant.
            setFocusCell((prev) => (prev?.row === row && prev?.col === col ? prev : { row, col }))
            // Gated exactly like onMouseEnter below. Without this, arrowing onto
            // a BOOKED cell paints a hover preview across nights canCheckinout
            // will reject — a state no mouse user can reach.
            if (interactive) handleCellHover(propId, key)
          },
        } : {})}
        onClick={interactive ? (e) => {
          popupOriginRef.current = { row, col }
          handleCellClick(propId, key, e.currentTarget as HTMLElement)
        } : undefined}
        onMouseEnter={interactive ? () => handleCellHover(propId, key) : undefined}
      >
        {priceText && <span className="hr-multical-price">{priceText}</span>}
      </td>
    )
  }

  const propertyLink = (p: MultiCalProperty) =>
    options.propertyLinkable && p.slug ? resolvePropertyLink({ feed: hrefFeed, slug: p.slug, pdpRoute }) : undefined

  return (
    <div className="hr-multical-wrap" ref={wrapRef}>
      {notice && (
        <div className={`hr-multical-notice${notice.type === 'danger' ? ' is-danger' : ''}`} role="alert">
          <span>{notice.text}</span>
          <button
            type="button"
            className="hr-multical-notice-close"
            aria-label="Dismiss"
            onFocus={() => { noticeHoverRef.current = true }}
            onBlur={() => { noticeHoverRef.current = false }}
            onClick={() => { noticeHoverRef.current = false; setNotice(null) }}
          >×</button>
        </div>
      )}

      {/* The shell is mounted whenever selection is possible, even with nothing
          selected. A polite live region that enters the DOM together with its
          first text announces nothing — it has to already be there, and empty,
          for the change to be spoken. */}
      {selectable && (
        <div className={`hr-multical-selbar${selection ? '' : ' is-empty'}`}>
          <span className="hr-multical-selbar-text" aria-live="polite">
            {selection
              ? (selection.checkout
                ? `${DateTime.fromISO(selection.checkin).toFormat('MMM d')} → ${DateTime.fromISO(selection.checkout).toFormat('MMM d')}`
                : `Check-in ${DateTime.fromISO(selection.checkin).toFormat('MMM d')} — pick a checkout date`)
              : ''}
          </span>
          {selection && options.showClearButton && (
            <button
              type="button"
              className="hr-multical-selbar-clear"
              onClick={() => {
                // This button unmounts itself, so hand focus back to the grid
                // rather than letting it fall to <body> — to the cell the
                // roving tabstop is on NOW. `popupOriginRef` is wherever a
                // range was last completed, not where the user has since moved.
                const back = tabstop
                clearSelection()
                if (back) { setFocusCell(back); pendingFocusRef.current = back }
              }}
            >
              {options.clearButtonText || 'Clear Dates'}
            </button>
          )}
        </div>
      )}

      <div
        className={`hr-multical-scroll${options.stickyHeader ? ' sticky-header' : ''}`}
        // #686: cap height from config (overrides the CSS 70vh fallback). Always
        // set inline — incl. `none` (unset) so it wins over the stylesheet rule.
        style={{ maxHeight: options.maxHeight.unit === 'none' ? 'none' : `${options.maxHeight.value}${options.maxHeight.unit}` }}
      >
        {/* role="grid" only when the cells are actually operable. A read-only
            strip stays a plain table, so the default-off widget's semantics —
            and how a screen reader browses it — are untouched. */}
        <table
          className="hr-multical-table"
          {...(selectable
            ? {
                role: 'grid',
                'aria-label': options.section?.title || 'Availability by property',
                /* "Load more" means the rendered rows are a subset, and without
                   a count AT announces "row 3 of 3" on a 200-property feed. -1
                   is the ARIA value for "total unknown", which is precisely the
                   case while more pages remain. */
                'aria-rowcount': hasNextPage ? -1 : properties.length,
              }
            : {})}
        >
          <thead>
            {/* Month band */}
            <tr className="hr-multical-month-row">
              <th className="hr-multical-corner" rowSpan={2} />
              {monthSpans.map((m, i) => (
                <th key={i} className="hr-multical-month" colSpan={m.count}>
                  {m.label}
                </th>
              ))}
            </tr>
            {/* Day band */}
            <tr className="hr-multical-day-row">
              {dates.map((key) => {
                const dt = DateTime.fromISO(key)
                const weekend = dt.weekday > 5
                return (
                  <th key={key} className={`hr-multical-day${weekend ? ' is-weekend' : ''}`}>
                    <span className="hr-multical-day-num">{dt.toFormat('d')}</span>
                    <span className="hr-multical-day-wd">{dt.toFormat('ccc')}</span>
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {properties.map((p, row) => {
              const link = propertyLink(p)
              const href = link?.href
              const PropCell = href ? 'a' : 'div'
              /* Feed currency first (#1078, the #994 shape): Central sends each
                 row's currency_code since central#3125, and the stored setting
                 is an indistinguishable-from-default 'USD'. */
              const currency = propertyCurrency(p, options.currency)
              return (
                <tr key={p.id} className="hr-multical-row" {...(selectable ? { 'aria-rowindex': row + 1 } : {})}>
                  {/* The row header is column -1 of the roving model. When
                      `propertyLinkable` is on (the default) it holds an <a>, and
                      leaving that as an ordinary tabstop would make the grid N
                      tabstops instead of one; dropping it to tabIndex -1 with no
                      home in the model would take away a link the keyboard can
                      reach today. So it joins the grid. */}
                  <th
                    className="hr-multical-prop"
                    {...(selectable ? {
                      role: 'rowheader',
                      'data-hr-cell': `${row}:${HEADER_COL}`,
                      tabIndex: tabstop?.row === row && tabstop?.col === HEADER_COL ? 0 : -1,
                      onKeyDown: (e: React.KeyboardEvent<HTMLElement>) => handleGridKeyDown({ row, col: HEADER_COL }, e),
                      onFocus: () => setFocusCell({ row, col: HEADER_COL }),
                    } : {})}
                  >
                    <PropCell
                      className="hr-multical-prop-inner"
                      {...(href ? { href } : {})}
                      {...(link?.external ? { rel: propertyLinkRel(link) } : {})}
                      {...(selectable && href ? { tabIndex: -1 } : {})}
                    >
                      {options.showThumbnail && (
                        <span className="hr-multical-thumb">
                          {p.thumbnail ? (
                            <img src={getProxiedImageUrl(p.thumbnail, 120, 80)} alt={p.title} loading="lazy" />
                          ) : (
                            <span className="hr-multical-thumb-ph" />
                          )}
                        </span>
                      )}
                      <span className="hr-multical-prop-title">{p.title}</span>
                    </PropCell>
                  </th>
                  {dates.map((key, col) => renderCell(p.id, p.title, key, p.availability[key], currency, row, col))}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* Not role="menu" any more: it held two plain <button>s, which is an
          invalid menu, and a menu implies arrow-key semantics nobody wants for
          two actions. A labelled group of buttons with focus moved in on open,
          Escape to cancel and focus handed back to the originating cell is what
          this actually is. */}
      {selectable && popup && selection?.checkout && (
        <div
          ref={popupRef}
          className="hr-multical-popup"
          style={{ top: popup.top, left: popup.left }}
          role="group"
          aria-label="Selected dates"
          onKeyDown={(e) => { if (e.key === 'Escape') { e.preventDefault(); closePopup() } }}
        >
          <button type="button" className="hr-multical-popup-item" onClick={handleReserve}>
            {options.reserveButtonText || 'Reserve'}
          </button>
          <button type="button" className="hr-multical-popup-item" onClick={closePopup}>
            {options.clearButtonText || 'Clear Dates'}
          </button>
        </div>
      )}

      {hasNextPage && (
        <div className="hr-multical-loadmore">
          <button
            type="button"
            className="hr-multical-loadmore-btn"
            disabled={isFetchingNextPage}
            onClick={() => fetchNextPage()}
          >
            {isFetchingNextPage ? 'Loading…' : 'Load more properties'}
          </button>
        </div>
      )}
    </div>
  )
}
